import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

interface DersNotu {
  id: string;
  ogrenci_id: string;
  not_tipi: string | null;
  not_metni: string;
  basari_durumu: string | null;
  not_tarihi: string | null;
  olusturma_tarihi: string | null;
  ogrenci?: { ogrenci_adi: string };
}

interface Student { id: string; ogrenci_adi: string; }

const typeColors: Record<string, string> = {
  Olumlu: 'bg-green-100 text-green-700',
  Olumsuz: 'bg-red-100 text-red-700',
  Nötr: 'bg-slate-100 text-slate-600',
};
const typeIcons: Record<string, string> = {
  Olumlu: 'ri-thumb-up-line text-green-500',
  Olumsuz: 'ri-thumb-down-line text-red-500',
  Nötr: 'ri-information-line text-slate-400',
};

export default function DersNotlariPage() {
  const { ogretmenRecord } = useAuth();
  const [data, setData] = useState<DersNotu[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [filterStudent, setFilterStudent] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ studentId: '', note: '', type: 'Nötr', basari: '' });

  const fetchData = useCallback(async () => {
    if (!ogretmenRecord?.id) { setLoading(false); return; }
    const [{ data: notes }, { data: s }] = await Promise.all([
      supabase.from('egtpanel_ders_notlari')
        .select('*, ogrenci:egtpanel_ogrenciler(ogrenci_adi)')
        .eq('ogretmen_id', ogretmenRecord.id)
        .order('olusturma_tarihi', { ascending: false }),
      supabase.from('egtpanel_ogrenciler').select('id, ogrenci_adi').eq('atanan_ogretmen_id', ogretmenRecord.id).order('ogrenci_adi'),
    ]);
    setData((notes || []) as DersNotu[]);
    setStudents(s || []);
    setLoading(false);
  }, [ogretmenRecord]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = data.filter(n => !filterStudent || n.ogrenci_id === filterStudent);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ogretmenRecord?.id || !form.studentId || !form.note) return;
    setSaving(true);
    await supabase.from('egtpanel_ders_notlari').insert({
      ogrenci_id: form.studentId,
      ogretmen_id: ogretmenRecord.id,
      not_tipi: form.type,
      not_metni: form.note,
      basari_durumu: form.basari || null,
      not_tarihi: new Date().toISOString().split('T')[0],
    });
    setSaving(false);
    setShowModal(false);
    setForm({ studentId: '', note: '', type: 'Nötr', basari: '' });
    fetchData();
  };

  if (loading) return (
    <div className="p-6 flex items-center justify-center py-32">
      <i className="ri-loader-4-line text-3xl text-slate-300 animate-spin" />
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Ders Notları</h1>
          <p className="text-slate-500 text-sm mt-1">Öğrenci gelişim notlarınızı kaydedin</p>
        </div>
        <button onClick={() => setShowModal(true)} className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors whitespace-nowrap cursor-pointer">
          <i className="ri-add-line" /> Not Ekle
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 p-4">
        <select value={filterStudent} onChange={e => setFilterStudent(e.target.value)} className="w-full sm:w-64 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20">
          <option value="">Tüm Öğrenciler</option>
          {students.map(s => <option key={s.id} value={s.id}>{s.ogrenci_adi}</option>)}
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-100 p-16 text-center">
          <i className="ri-sticky-note-line text-5xl text-slate-200 block mb-3" />
          <p className="text-slate-500 font-medium">Henüz not eklenmemiş</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(n => {
            const ogrenci = n.ogrenci as {ogrenci_adi:string} | undefined;
            const tipi = n.not_tipi || 'Nötr';
            return (
              <div key={n.id} className="bg-white rounded-xl border border-slate-100 p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-purple-600 text-white font-bold text-sm">
                      {ogrenci?.ogrenci_adi?.charAt(0) || '?'}
                    </div>
                    <div>
                      <div className="font-medium text-slate-800">{ogrenci?.ogrenci_adi || '—'}</div>
                      <div className="text-xs text-slate-400">{n.not_tarihi || '—'}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className={typeIcons[tipi] || 'ri-information-line text-slate-400'} />
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${typeColors[tipi] || 'bg-slate-100 text-slate-600'}`}>{tipi}</span>
                  </div>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">{n.not_metni}</p>
                {n.basari_durumu && <p className="text-xs text-slate-400 mt-2">Başarı: {n.basari_durumu}</p>}
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <h2 className="text-lg font-bold text-slate-800">Yeni Not Ekle</h2>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer"><i className="ri-close-line" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Öğrenci *</label>
                <select required value={form.studentId} onChange={e => setForm(p => ({...p, studentId: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20">
                  <option value="">— Seçiniz —</option>
                  {students.map(s => <option key={s.id} value={s.id}>{s.ogrenci_adi}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Not Tipi</label>
                <div className="flex gap-2">
                  {['Olumlu', 'Olumsuz', 'Nötr'].map(t => (
                    <button key={t} type="button" onClick={() => setForm(p => ({...p, type: t}))}
                      className={`flex-1 py-2 rounded-lg text-xs font-medium border transition-colors cursor-pointer ${form.type === t ? (typeColors[t] + ' border-transparent') : 'text-slate-600 border-slate-200 hover:border-slate-300'}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Başarı Durumu</label>
                <input value={form.basari} onChange={e => setForm(p => ({...p, basari: e.target.value}))} placeholder="İyi, Orta, Zayıf..." className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Not *</label>
                <textarea required value={form.note} onChange={e => setForm(p => ({...p, note: e.target.value}))} rows={4} maxLength={500} placeholder="Öğrenci hakkında gelişim notunuzu yazın..." className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 resize-none" />
                <p className="text-xs text-slate-400 mt-1">{form.note.length}/500</p>
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 cursor-pointer">İptal</button>
                <button type="submit" disabled={saving} className="flex-1 py-2.5 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 cursor-pointer">
                  {saving ? <i className="ri-loader-4-line animate-spin" /> : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}